import { useRef, useEffect, useState, Suspense, type KeyboardEvent } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  TextField,
  CircularProgress,
  Chip,
  Collapse,
} from '@mui/material';
import Close from '@mui/icons-material/CloseOutlined';
import Send from '@mui/icons-material/SendOutlined';
import DeleteSweep from '@mui/icons-material/DeleteOutlined';
import SmartToy from '@mui/icons-material/SmartToyOutlined';
import ExpandMore from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLess from '@mui/icons-material/ExpandLessOutlined';
import { Bot as BotIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { LazyMarkdown } from '@/components/LazyMarkdown';
import { HELP_LINK_SCHEMES, helpLinkComponents } from '@/components/helpLinkMarkdown';
import { useResizablePanelWidth } from '@/components/useResizablePanelWidth';
import { useAssistant, type ChatMessage } from './useAssistant';
import { useAssistantAvailability } from './useAssistantAvailability';
import type { ToolInvocation } from './assistantApi';

const DEFAULT_DRAWER_WIDTH = 440;

// ---------------------------------------------------------------------------
// Tool Badge (expandable tool invocation list)
// ---------------------------------------------------------------------------

function ToolBadges({ tools }: { tools: ToolInvocation[] }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());

  if (tools.length === 0) return null;

  const toggleTool = (index: number) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Chip
        size="small"
        icon={expanded ? <ExpandLess /> : <ExpandMore />}
        label={`${tools.length} tool${tools.length > 1 ? 's' : ''} used`}
        variant="outlined"
        onClick={() => setExpanded((prev) => !prev)}
        sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
      />
      <Collapse in={expanded}>
        <Box sx={{ mt: 0.5, pl: 1 }}>
          {tools.map((tool, i) => (
            <Box
              key={i}
              sx={{
                mb: 0.5,
                borderRadius: 0.5,
                bgcolor: 'action.hover',
                fontSize: '0.7rem',
                fontFamily: 'monospace',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', p: 0.75, cursor: 'pointer' }}
                onClick={() => toggleTool(i)}
              >
                <Typography variant="caption" fontWeight={600}>
                  {tool.toolName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {tool.durationMs}ms
                </Typography>
                {expandedTools.has(i) ? (
                  <ExpandLess sx={{ fontSize: 14, ml: 'auto' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 14, ml: 'auto' }} />
                )}
              </Box>
              <Collapse in={expandedTools.has(i)}>
                <Box sx={{ px: 0.75, pb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    Input:
                  </Typography>
                  <Box
                    sx={{
                      p: 0.5,
                      borderRadius: 0.5,
                      bgcolor: 'background.paper',
                      fontSize: '0.65rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: 120,
                      overflow: 'auto',
                    }}
                  >
                    {tool.input}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.25 }}>
                    Output:
                  </Typography>
                  <Box
                    sx={{
                      p: 0.5,
                      borderRadius: 0.5,
                      bgcolor: 'background.paper',
                      fontSize: '0.65rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {tool.output}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Message Bubble
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  if (message.isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1, px: 1.5 }}>
        <SmartToy sx={{ fontSize: 18, color: 'text.secondary' }} />
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Thinking…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        py: 0.5,
        px: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
          bgcolor: isUser ? 'primary.main' : 'action.hover',
          color: isUser ? 'primary.contrastText' : 'text.primary',
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}
          >
            {message.content}
          </Typography>
        ) : (
          <Box
            sx={{
              '& p': { m: 0, mb: 0.5, fontSize: '0.875rem', lineHeight: 1.5, '&:last-child': { mb: 0 } },
              '& h1,& h2,& h3': { mt: 1, mb: 0.5, fontSize: '0.95rem', fontWeight: 600 },
              '& ul,& ol': { m: 0, pl: 2.5, fontSize: '0.875rem' },
              '& li': { mb: 0.25 },
              '& code': {
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                bgcolor: 'action.selected',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
              },
              '& pre': {
                p: 1,
                borderRadius: 1,
                bgcolor: 'action.selected',
                overflowX: 'auto',
                '& code': { p: 0, bgcolor: 'transparent' },
              },
              '& table': {
                borderCollapse: 'collapse',
                fontSize: '0.8rem',
                my: 0.5,
                width: '100%',
              },
              '& th,& td': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1,
                py: 0.5,
                textAlign: 'left',
              },
              '& th': { fontWeight: 600, bgcolor: 'action.hover' },
              overflowX: 'auto',
              wordBreak: 'break-word',
            }}
          >
            <Suspense
              fallback={
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}
                >
                  {message.content}
                </Typography>
              }
            >
              {/* The assistant cites the help sections it used as help: links, which open the help panel. */}
              <LazyMarkdown components={helpLinkComponents} keepSchemes={HELP_LINK_SCHEMES}>
                {message.content}
              </LazyMarkdown>
            </Suspense>
          </Box>
        )}
      </Box>
      {!isUser && message.toolsUsed && <ToolBadges tools={message.toolsUsed} />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export function AiChatPanel() {
  const { t } = useTranslation();
  const canAsk = useHasPermission('NexoAI.Ask');
  const available = useAssistantAvailability(canAsk);
  const { messages, send, clearChat, isLoading, context } = useAssistant();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Drag-to-resize on the left edge; the panel is app-global (always-mounted
  // Layout), so the saved width is global too — not per form route.
  const { width, ResizeHandle } = useResizablePanelWidth('ai-assistant', {
    defaultWidth: DEFAULT_DRAWER_WIDTH,
    storageScope: 'global',
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    send(input);
    setInput('');
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Context chip label
  const contextLabel = context.entityType
    ? `${context.module ?? ''} / ${context.entityType}${context.entityId ? ` #${context.entityId.slice(0, 8)}` : ''}`
    : context.module || undefined;

  if (!available) return null;

  return (
    <>
      {/* Toggle button (rendered in AppBar) */}
      <IconButton color="inherit" onClick={() => setOpen(true)} title="AI Assistant">
        <BotIcon size={20} />
      </IconButton>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent' } },
          // The question field takes the focus once the panel has slid in. Not autoFocus: the drawer's focus
          // trap moves the focus to the panel itself when it opens, after the field's own autoFocus.
          transition: { onEntered: () => inputRef.current?.focus() },
          paper: {
            sx: {
              width,
              display: 'flex',
              flexDirection: 'column',
              top: '59px',
              height: 'calc(100% - 58px)',
            },
          },
        }}
      >
        <ResizeHandle />

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              AI Assistant
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {messages.length > 0 && (
              <IconButton
                size="small"
                onClick={clearChat}
                title={t('common.clear', 'Clear')}
              >
                <DeleteSweep fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              title={t('common.close', 'Close')}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Context chip */}
        {contextLabel && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Chip
              size="small"
              label={contextLabel}
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          </Box>
        )}

        <Divider />

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, py: 1 }}>
          {messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 1,
                px: 3,
              }}
            >
              <SmartToy sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Ask me anything about the system — entities, processes, fields, state machines, and more.
              </Typography>
            </Box>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        <Divider />

        {/* Input */}
        <Box sx={{ p: 1.5, flexShrink: 0 }}>
          <TextField
            fullWidth
            inputRef={inputRef}
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask the AI assistant…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    size="small"
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    color="primary"
                  >
                    {isLoading ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                  </IconButton>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Drawer>
    </>
  );
}
